import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { fmtDiaNum, fmtMesCorto } from "@/lib/format";
import { Badge } from "@/components/ui/badge";

const TIPO_LABEL = { nacional: "Nacional", local: "Local" };

export function FestivoFila({ festivo, centro, showTipo = true }) {
  return (
    <div
      className="flex items-stretch gap-3 rounded-xl bg-zinc-100/80 p-3 dark:bg-zinc-800/60"
      data-testid={`festivo-item-${festivo.id}`}
    >
      <div className="w-1 shrink-0 self-stretch rounded-full bg-indigo-400/90" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-snug text-zinc-900 dark:text-zinc-100">
          {festivo.nombre}
        </p>
        <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
          {festivo.centro || centro || "Tu centro"}
        </p>
        {showTipo && festivo.tipo && (
          <Badge variant="outline" className="mt-1.5 h-5 border-zinc-200 px-1.5 text-[10px] font-medium text-zinc-500">
            {TIPO_LABEL[festivo.tipo] || festivo.tipo}
          </Badge>
        )}
      </div>
      <div
        className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl border border-zinc-200/90 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900"
        aria-hidden
      >
        <span className="text-[10px] font-bold uppercase tracking-wide text-rose-500">
          {fmtMesCorto(festivo.fecha)}
        </span>
        <span className="font-display text-xl font-semibold leading-none text-zinc-900 dark:text-zinc-100">
          {fmtDiaNum(festivo.fecha)}
        </span>
      </div>
    </div>
  );
}

export function FestivosLista({ data, showHeader = true }) {
  if (!data?.festivos?.length) return null;

  return (
    <section
      className="overflow-hidden rounded-2xl border border-indigo-100/90 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
      data-testid="festivos-lista"
    >
      {showHeader && (
        <div className="flex items-center justify-between border-b border-indigo-100/80 px-5 py-4 dark:border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 text-white shadow-sm">
              <CalendarDays className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold text-zinc-900 dark:text-zinc-100">Festivos</h2>
              {data.centro && <p className="text-xs text-zinc-500">{data.centro}</p>}
            </div>
          </div>
          {data.total > 0 && (
            <span className="text-xs text-zinc-500">
              {data.total} festivo{data.total !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}

      <div className="space-y-2 px-4 py-4">
        {data.festivos.map((festivo) => (
          <FestivoFila key={festivo.id} festivo={festivo} centro={data.centro} />
        ))}
      </div>
    </section>
  );
}

export default function FestivosEmpresaPanel({ compact = false, className = "" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = compact
          ? { solo_proximos: true, limit: 3 }
          : { year: new Date().getFullYear(), limit: 50 };
        const { data: res } = await api.get("/trabajador/festivos", { params });
        if (!cancelled) setData(res);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [compact]);

  if (loading) {
    return (
      <section
        className={`overflow-hidden rounded-2xl border border-indigo-100/90 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900 ${className}`}
        data-testid="festivos-panel-loading"
      >
        <div className="px-5 py-8 text-center text-sm text-zinc-400">Cargando festivos…</div>
      </section>
    );
  }

  if (!data?.festivos?.length) {
    if (compact) return null;
    return (
      <section
        className={`overflow-hidden rounded-2xl border border-indigo-100/90 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900 ${className}`}
        data-testid="festivos-panel-empty"
      >
        <div className="flex items-center gap-3 border-b border-indigo-100/80 px-5 py-4 dark:border-zinc-800">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 text-white shadow-sm">
            <CalendarDays className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-zinc-900 dark:text-zinc-100">Festivos</h2>
            {data?.centro && <p className="text-xs text-zinc-500">{data.centro}</p>}
          </div>
        </div>
        <div className="px-5 py-8 text-center text-sm text-zinc-500">
          No hay festivos registrados para tu centro este año.
        </div>
      </section>
    );
  }

  const hayMas = compact && data.total > data.festivos.length;

  return (
    <section
      className={`overflow-hidden rounded-2xl border border-indigo-100/90 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900 ${className}`}
      data-testid="festivos-panel"
    >
      <div className="flex items-center justify-between border-b border-indigo-100/80 px-5 py-4 dark:border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 text-white shadow-sm">
            <CalendarDays className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-zinc-900 dark:text-zinc-100">Festivos</h2>
            {data.centro && <p className="text-xs text-zinc-500">{data.centro}</p>}
          </div>
        </div>
      </div>

      <div className="space-y-2 px-4 py-4">
        {data.festivos.map((festivo) => (
          <FestivoFila key={festivo.id} festivo={festivo} centro={data.centro} />
        ))}
      </div>

      {(hayMas || compact) && (
        <div className="border-t border-indigo-100/80 px-4 py-3 dark:border-zinc-800">
          <Link
            to="/mis-festivos"
            className="inline-flex items-center rounded-full bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
            data-testid="festivos-ver-mas"
          >
            Ver más
            {hayMas && <span className="ml-1.5 text-xs text-zinc-500">({data.total})</span>}
          </Link>
        </div>
      )}
    </section>
  );
}
